import ActionDialog from "@dashboard/components/ActionDialog/ActionDialog";
import { type ConfirmButtonTransitionState } from "@dashboard/components/ConfirmButton/ConfirmButton";
import { type AtticErrorFragment } from "@dashboard/graphql";
import { Box, Input, Text } from "@saleor/macaw-ui-next";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";

import { getAtticInventoryErrorMessage } from "../../errors";
import { stockMessages } from "../../messages";
import { parseInteger } from "../../utils";

export interface AtticReserveDialogProps {
  open: boolean;
  reserved: number;
  reason: string;
  onHand: number;
  errors: AtticErrorFragment[];
  confirmButtonState: ConfirmButtonTransitionState;
  onClose: () => void;
  onConfirm: (quantity: number, reason: string) => void;
}

/** The absolute hold, not a delta: what the API takes. */
export const AtticReserveDialog = ({
  open,
  reserved,
  reason,
  onHand,
  errors,
  confirmButtonState,
  onClose,
  onConfirm,
}: AtticReserveDialogProps) => {
  const intl = useIntl();
  const [quantity, setQuantity] = useState(String(reserved));
  const [why, setWhy] = useState(reason);

  useEffect(() => {
    if (open) {
      setQuantity(String(reserved));
      setWhy(reason);
    }
  }, [open, reserved, reason]);

  const parsed = parseInteger(quantity);
  const valid = parsed !== null && parsed >= 0 && parsed <= onHand;
  const [refusal] = errors;

  return (
    <ActionDialog
      open={open}
      title={intl.formatMessage(stockMessages.reserveTitle)}
      confirmButtonState={confirmButtonState}
      disabled={!valid}
      onClose={onClose}
      onConfirm={() => {
        if (parsed !== null && valid) {
          onConfirm(parsed, why.trim());
        }
      }}
    >
      <Box display="flex" flexDirection="column" gap={4}>
        <Input
          type="number"
          min={0}
          max={onHand}
          label={intl.formatMessage(stockMessages.reserveQuantity)}
          value={quantity}
          onChange={event => setQuantity(event.target.value)}
          helperText={`0–${onHand}`}
          error={quantity !== "" && !valid}
          data-test-id="attic-reserve-quantity"
        />
        <Input
          label={intl.formatMessage(stockMessages.reserveReason)}
          value={why}
          onChange={event => setWhy(event.target.value)}
          data-test-id="attic-reserve-reason"
        />
        {refusal && (
          <Text size={3} color="critical1" data-test-id="attic-reserve-error">
            {getAtticInventoryErrorMessage(refusal, intl)}
          </Text>
        )}
      </Box>
    </ActionDialog>
  );
};
